import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

/**
 * Sprint 24.9 (Section G) — MCP response-size telemetry. Mirrors the
 * `get_tool_response_stats` tool response: top tools by total response size,
 * powering the Settings → "MCP usage" section so the owner can see which tools
 * cost hosts the most bytes per turn.
 */
export interface ToolResponseStatRow {
  tool_name: string;
  calls: number;
  total_bytes: number;
  avg_bytes: number;
  max_bytes: number;
  total_tokens_est: number;
  errors: number;
}

export interface ToolResponseStats {
  tools: ToolResponseStatRow[];
  totals: { calls: number; total_bytes: number; total_tokens_est: number };
  since: string;
  top_n: number;
}

const EMPTY: ToolResponseStats = {
  tools: [],
  totals: { calls: 0, total_bytes: 0, total_tokens_est: 0 },
  since: "all-time",
  top_n: 10,
};

/**
 * Fetch the top-N tools by response size. Owner-only on the server
 * (get_tool_response_stats is in OWNER_ONLY_TOOLS); callers gate the mount on
 * `useIsOwner`. `enabled` lets the page skip the fetch for non-owners entirely.
 */
export function useToolResponseStats(enabled = true, topN = 10) {
  return useQuery<ToolResponseStats>({
    queryKey: ["tool-response-stats", topN],
    queryFn: async () => {
      const result = await mcpClient.toolsCall<ToolResponseStats>("get_tool_response_stats", {
        top_n: topN,
      });
      return (result.structuredContent ?? EMPTY) as ToolResponseStats;
    },
    enabled,
    staleTime: 30_000,
  });
}
